<template>
    <div class="md:container">
        <Xandr
            xandr-wrapper="sponsored_course_headerWrapper"
            xandr-slot="sponsored_course_header"
            is-sponsored-header
        ></Xandr>
        <div class="relative flex w-full flex-col cursor-default mb-6 md:rounded-lg shadow">
            <div :class="['relative md:rounded-lg', { 'bg-black': courseSponsoringHeader }]">
                <div
                    :class="[
                        {
                            purple: !courseSponsoringHeader,
                            'opacity-75': courseSponsoringHeader,
                        },
                        'bg-center bg-no-repeat bg-cover background md:rounded-lg absolute left-0 top-0 right-0 bottom-0 transition-all ease-linear duration-300',
                    ]"
                    :style="courseSponsoringHeaderStyle"
                ></div>

                <div :class="['w-full pt-4 md:pt-6 md:pb-6 md:px-6', { 'text-white': courseSponsoringHeader }]">
                    <div class="relative flex items-start w-full flex-col">
                        <div class="max-md:container">
                            <h1
                                style="word-break: break-word !important"
                                class="mb-4 md:mb-2 line-clamp-3 md:line-clamp-2 font-display-3 lg:font-display-2"
                                :class="{
                                    'md:pr-24': inCourse(course.id),
                                    'md:pr-12': !inCourse(course.id),
                                }"
                                v-text="`${course.name} ${t('uni.course_one')}`"
                            ></h1>
                            <div
                                :class="[
                                    'flex flex-col items-start w-full md:flex-row md:mb-6',
                                    courseSponsoringHeader !== null ? 'text-white' : ' text-slate-600',
                                ]"
                            >
                                <p class="flex flex-row items-center mb-2 md:mb-0 md:pr-4">
                                    <i class="mr-2 icon icon-people"></i><span v-text="course.members"></span>
                                </p>
                                <p
                                    v-if="course.university"
                                    class="flex flex-row items-center mb-2 md:mb-0 md:pr-4"
                                >
                                    <i class="mr-2 icon icon-university"></i><span> {{ universityCourseName }}</span>
                                </p>
                                <p
                                    v-if="course.confirmedExamDate"
                                    :class="[
                                        isLoggedIn ? 'cursor-pointer' : 'cursor-default',
                                        'flex flex-row items-center mb-6 md:mb-0',
                                    ]"
                                    @click="confirmExamReport"
                                >
                                    <i class="mr-2 icon icon-calendar-event"></i
                                    ><span
                                        :class="[isLoggedIn ? 'hover:opacity-75' : '']"
                                        v-text="t('exams.next_date_label') + ' ' + course.confirmedExamDate"
                                    ></span>
                                </p>
                            </div>
                        </div>
                        <div class="flex flex-row justify-between items-center w-full mb-6 md:mb-0 max-md:container">
                            <div :class="['flex relative w-full md:w-auto']">
                                <!-- native modifier has been removed, please confirm whether the function has been affected  -->
                                <BtnCta
                                    variant="secondary"
                                    :class="[
                                        { 'px-3.5': hasJoinedCourse || joiningCourse },
                                        'cta-group w-full md:w-auto',
                                    ]"
                                    :icon="hasJoinedCourse && !joiningCourse ? 'checkmark-stroke' : null"
                                    :label="btnLabel"
                                    :disabled="hasJoinedCourse"
                                    @click="join"
                                />
                                <BtnCta
                                    v-tooltip="{
                                        content: t('sidebar.upload'),
                                        placement: 'bottom',
                                    }"
                                    class="protected-auth protected-verified mx-4 hidden lg:flex"
                                    icon="add-file"
                                    :href="route('document.universities.create', { courseId: course.id })"
                                />
                                <div
                                    v-if="showTooltip"
                                    :class="[
                                        'header-tooltip absolute bordered rounded-lg p-3 bg-slate-600 text-white whitespace-normal leading-6',
                                    ]"
                                >
                                    <span class="triangle border-transparent border-b-slate-600 absolute"></span>
                                    <div
                                        class="closeBtn absolute cursor-pointer flex justify-center items-center rounded-full bg-slate-400 w-6 h-6"
                                        @click="closeTooltip"
                                    >
                                        <span class="relative icon icon-close text-white w-4 h-4"></span>
                                    </div>
                                    <span>{{ t('header.tooltip') }}</span>
                                </div>
                            </div>

                            <portal-target name="sponsoringBtn"></portal-target>
                        </div>
                    </div>
                </div>
            </div>

            <CourseShowMenu
                :with-banner="courseSponsoringHeader !== null"
                :class="{
                    'md:text-white text-black': courseSponsoringHeader,
                }"
            ></CourseShowMenu>
            <!--  Xandr Course Sponsoring ad -->
            <XandrSponsoredHeader
                v-if="sponsoringAd"
                :xandr-ad="sponsoringAd"
            />
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import { get } from 'lodash-es';
import { Xandr, XandrSponsoredHeader } from '@components/Xandr';
import GtmMixin, { GtmEventName } from '@/mixins/gtmMixin';
import route from '@router/route';
import { trackEvent } from '@/modules/tracking';
import { useTranslation } from 'i18next-vue';
import CourseShowMenu from './CourseShowMenu.vue';

const UNIVERSITY_COURSE_NAME_TRUNCATE_LENGTH = 50;

export default {
    name: 'CourseShowHeader',
    components: {
        CourseShowMenu,
        Xandr,
        XandrSponsoredHeader,
    },
    extends: GtmMixin,
    setup() {
        const { t } = useTranslation('course');
        return { t };
    },
    data() {
        return {
            tooltipDismissed: false,
        };
    },
    computed: {
        ...mapGetters({
            screen: 'ui/screen',
            course: 'course/course',
            sponsoringAd: 'xandr/xandrSponsoringAd',
            leavingCourse: 'course/leaveProgress',
            joiningCourse: 'course/joinProgress',
            userCourses: 'user/courses',
            inCourse: 'user/inCourse',
        }),
        ...mapGetters({ user: 'auth/user' }),
        ...mapGetters('ui', ['screenSize']),
        ...mapGetters('auth', ['isLoggedIn']),
        courseSponsoringHeader() {
            const header = get(this.sponsoringAd, 'header_background', null);
            const mobileHeader = get(this.sponsoringAd, 'mobile_header_background', null);

            return this.screen.maxMD ? mobileHeader : header;
        },
        courseSponsoringHeaderStyle() {
            if (this.courseSponsoringHeader) {
                return {
                    'background-image': `url(${this.courseSponsoringHeader})`,
                };
            }
            return {};
        },
        hasJoinedCourse() {
            return this.joiningCourse || this.inCourse(this.course.id);
        },
        btnLabel() {
            if (this.joiningCourse) {
                return this.t('header.cta_progress');
            }
            if (!this.inCourse(this.course.id)) {
                return this.t('header.cta');
            }
            return this.t('header.cta_disabled');
        },
        showTooltip() {
            return !this.isLoggedIn && !this.tooltipDismissed;
        },
        universityCourseName() {
            const name = this.course?.university?.name ?? '';
            const maxVisibleLength = UNIVERSITY_COURSE_NAME_TRUNCATE_LENGTH ?? 50;
            if (name && name.length > maxVisibleLength) {
                return `${name.substring(0, maxVisibleLength)}...`;
            }

            return name;
        },
    },
    methods: {
        route,
        closeTooltip() {
            this.tooltipDismissed = true;
        },
        async join() {
            if (!this.isLoggedIn) {
                this.$store.dispatch('ui/openRegistration', {
                    authTrigger: 'joinCourse',
                });
            } else if (
                this.isLoggedIn &&
                !this.inCourse(this.course.id) &&
                !(this.leavingCourse || this.joiningCourse)
            ) {
                await this.$store.dispatch('course/join', {
                    course: this.course,
                    fromCoursePage: true,
                });
                this.pushGtmEvent(GtmEventName.JOIN_COURSE);

                if (this.$route?.query?.dashboard === '1') {
                    trackEvent('join_course', 'recommended_course_join', `${this.course.id}`);
                }
            }
        },
        confirmExamReport() {
            if (this.isLoggedIn && this.inCourse(this.course.id)) {
                this.$modal.open(
                    defineAsyncComponent(() => import('@/components/ModalExamDateConfirmation')),
                    {
                        courseId: this.course.id,
                        confirmedExamDate: this.course.confirmedExamDate,
                    },
                );
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.background {
    &.purple {
        background-image: url(/images/backgrounds/background-purple.svg);
    }
}
.header-tooltip {
    top: 132%;
    width: 230px;
    @apply z-[10];
    height: 120px;
    @screen md {
        left: 110%;
        top: -65%;
    }
}
.triangle {
    border-width: 0 10px 10px;
    top: -8%;
    left: 15%;
    @screen md {
        top: 45%;
        left: -13px;
        transform: rotate(-90deg);
    }
}

.closeBtn {
    top: -6%;
    left: 93%;
}
</style>
